/**
 * 표 인터랙션 컨트롤러 (P3-②③④) — 샘플 block-editor.html 패턴(D5)을 우리 모델/렌더/직렬화에 이식.
 *  - P3-②: 셀 드래그 선택 + .sel-cell + 단일 선택박스.
 *  - P3-③: 표 툴바(행/열 추가삭제·병합/분할·정렬·세로정렬·셀 배경/글자색·머리행/열).
 *  - P3-④: 열/행 리사이즈 핸들 + 행/열 추가 호버바 + Tab/화살표 셀 내비 + Enter 줄바꿈.
 *
 * 구조 연산 후: syncModelFromTableDom → 연산 → rerenderTable → 재포커스 → onChange(commit).
 * 속성(정렬/색)은 재렌더 없이 모델+DOM 직접 갱신(캐럿 유지).
 */

import { syncModelFromTableDom, rerenderTable } from "./render.js";
import { insertRow, insertCol, deleteRows, deleteCols, mergeCells, splitCell, moveRow, moveCol, expandRect } from "./ops.js";
import { fitWidths, effMinCol, HARD_MIN_COL } from "./fit.js";
import { extractGridClip, gridClipToHtml, gridClipToTSV, parseClipToGrid, pasteGridIntoModel, LUMIR_TABLE_MIME } from "./clipboard.js";

const FILL_COLORS = ["default", "gray", "brown", "red", "orange", "yellow", "green", "blue", "purple", "pink"];
const HEX = { gray: "#ebeced", brown: "#e9e5e3", red: "#fbe4e4", orange: "#f6e9d9", yellow: "#fbf3db", green: "#ddedea", blue: "#ddebf1", purple: "#eae4f2", pink: "#f4dfeb" };
const TEXT_HEX = { gray: "#9b9a97", brown: "#64473a", red: "#e03e3e", orange: "#d9730d", yellow: "#dfab01", green: "#4d6461", blue: "#0b6e99", purple: "#6940a5", pink: "#ad1a72" };
const MIN_COL = 40, MIN_ROW = 24;
// 전체/코너 스케일 하한 — compact(열 다수) 상태의 CSS min-width(HARD_MIN_COL)와 정합해 모델=렌더 일치.
const MIN_SCALE_COL = HARD_MIN_COL, MIN_SCALE_ROW = HARD_MIN_COL;

// 모바일 뷰포트: 폭 맞춤(fit-to-width) 대신 가로 스크롤 허용. CSS 브레이크포인트(table.css)와 일치시킬 것.
const MOBILE_MQ = "(max-width: 640px)";
function isMobileViewport() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(MOBILE_MQ).matches;
}

// 표 툴바 SVG 아이콘 (block-editor.html 세트 + vanilla 확장: 표정렬 tbl*, 글자색 tcolor).
const SVG_ICON = (p) => `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${p}</svg>`;
const TT_ICN = {
  rowAbove: '<path d="M8 2v4.5"/><path d="M5.8 4 8 1.8 10.2 4"/><path d="M3 9.5h10"/><path d="M3 12.5h10"/>',
  rowBelow: '<path d="M3 3.5h10"/><path d="M3 6.5h10"/><path d="M8 9.5V14"/><path d="M5.8 12 8 14.2 10.2 12"/>',
  rowDel: '<path d="M3 4.5h10"/><path d="M3 11.5h10"/><path d="M5.5 8h5"/>',
  colLeft: '<path d="M2 8h4.5"/><path d="M4 5.8 1.8 8 4 10.2"/><path d="M9.5 3v10"/><path d="M12.5 3v10"/>',
  colRight: '<path d="M3.5 3v10"/><path d="M6.5 3v10"/><path d="M9.5 8H14"/><path d="M12 5.8 14.2 8 12 10.2"/>',
  colDel: '<path d="M4.5 3v10"/><path d="M11.5 3v10"/><path d="M5.5 8h5"/>',
  merge: '<rect x="2" y="4.3" width="12" height="7.4" rx="1.3"/><path d="M5 6.5 6.6 8 5 9.5"/><path d="M11 6.5 9.4 8 11 9.5"/>',
  split: '<rect x="2" y="4.3" width="12" height="7.4" rx="1.3"/><path d="M8 4.3v7.4"/>',
  alignL: '<path d="M2.5 4.5h11"/><path d="M2.5 8h7"/><path d="M2.5 11.5h9"/>',
  alignC: '<path d="M2.5 4.5h11"/><path d="M4.5 8h7"/><path d="M3.5 11.5h9"/>',
  alignR: '<path d="M2.5 4.5h11"/><path d="M6.5 8h7"/><path d="M4.5 11.5h9"/>',
  valTop: '<path d="M2.5 2.5h11"/><rect x="6" y="4.5" width="4" height="7" rx="0.6"/>',
  valMid: '<rect x="6" y="4.5" width="4" height="7" rx="0.6"/><path d="M2.5 8h2.5"/><path d="M11 8h2.5"/>',
  valBot: '<path d="M2.5 13.5h11"/><rect x="6" y="4.5" width="4" height="7" rx="0.6"/>',
  fill: '<path fill="currentColor" stroke="none" d="M8 2.2c2.6 3.2 4.2 5.2 4.2 7.1a4.2 4.2 0 0 1-8.4 0C3.8 7.4 5.4 5.4 8 2.2z"/>',
  tcolor: '<path d="M4.3 12.5 8 3.5l3.7 9"/><path d="M5.8 9.4h4.4"/><path d="M3.5 14h9" stroke-width="2.2"/>',
  headerRow: '<rect x="2.5" y="3" width="11" height="10" rx="1"/><rect x="2.5" y="3" width="11" height="3" rx="1" fill="currentColor" stroke="none" opacity="0.28"/><path d="M2.5 6h11"/><path d="M2.5 9.5h11"/><path d="M6.3 6v7"/><path d="M9.7 6v7"/>',
  headerCol: '<rect x="2.5" y="3" width="11" height="10" rx="1"/><rect x="2.5" y="3" width="3.5" height="10" rx="1" fill="currentColor" stroke="none" opacity="0.28"/><path d="M6 3v10"/><path d="M9.5 3v10"/><path d="M2.5 6.3h11"/><path d="M2.5 9.7h11"/>',
  tblL: '<rect x="1.5" y="4" width="8" height="8" rx="1"/><path d="M13.5 3.5v9" opacity="0.4"/>',
  tblC: '<rect x="4" y="4" width="8" height="8" rx="1"/><path d="M2 3.5v9" opacity="0.35"/><path d="M14 3.5v9" opacity="0.35"/>',
  tblR: '<rect x="6.5" y="4" width="8" height="8" rx="1"/><path d="M2.5 3.5v9" opacity="0.4"/>',
  del: '<path d="M3 4.5h10"/><path d="M6.4 4.5V3.2c0-.4.3-.7.7-.7h1.8c.4 0 .7.3.7.7v1.3"/><path d="M4.7 4.5l.6 8.6c0 .5.4.9.9.9h3.6c.5 0 .9-.4.9-.9l.6-8.6"/><path d="M7 7v3.8"/><path d="M9 7v3.8"/>',
  insertCheckbox: '<rect x="2.5" y="2.5" width="11" height="11" rx="2.2"/><path d="M5.3 8.1 7.1 9.9 10.8 5.8"/>',
  orderedList: '<path d="M6.5 4.5h7"/><path d="M6.5 8h7"/><path d="M6.5 11.5h7"/><path d="M2.4 3.2v2.4"/><path d="M2 8.3h1.2l-1.2 1.5h1.3"/><path d="M2 11h1.1M2 12.4h1.1M3.1 11v1.4"/>',
  bulletList: '<circle cx="3.2" cy="4.5" r="0.9" fill="currentColor" stroke="none"/><circle cx="3.2" cy="8" r="0.9" fill="currentColor" stroke="none"/><circle cx="3.2" cy="11.5" r="0.9" fill="currentColor" stroke="none"/><path d="M6.5 4.5h7"/><path d="M6.5 8h7"/><path d="M6.5 11.5h7"/>',
};
TT_ICN.fillPop = TT_ICN.fill;
TT_ICN.tcolorPop = TT_ICN.tcolor;

export function attachTableControls(editorEl, opts) {
  opts = opts || {};
  const onChange = typeof opts.onChange === "function" ? opts.onChange : () => {};
  const tbl = opts.tables || {}; // 구성 토글: splitCells / cellBackgroundColor / cellTextColor / headers
  const LT = (opts.locale && opts.locale.table) || {}; // 로케일(표 툴바 툴팁)

  let selectedCells = [];
  let selecting = false, selAnchor = null, selTable = null;
  let activeTable = null, activeCell = null;

  // ---------- toolbar element ----------
  const bar = document.createElement("div");
  bar.className = "lumir-table-toolbar";
  bar.setAttribute("role", "toolbar");
  bar.setAttribute("aria-label", LT.toolbar || "표 서식 도구모음");
  bar.style.position = "absolute";
  bar.style.display = "none";
  bar.innerHTML = buildToolbarHtml();
  document.body.appendChild(bar);

  // ---------- helpers ----------
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));
  function cellEl(table, m) {
    return table.querySelector(`td.lumir-cell[data-r="${m.r}"][data-c="${m.c}"]`);
  }
  function focusEnd(el) {
    if (!el) return;
    el.focus();
    const r = document.createRange();
    r.selectNodeContents(el);
    r.collapse(false);
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(r);
  }
  function focusStart(el) {
    if (!el) return;
    el.focus();
    const r = document.createRange();
    r.selectNodeContents(el);
    r.collapse(true);
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(r);
  }
  /**
   * 표에서 위/아래 방향키로 인접 블록으로 탈출 — dir<0=이전 블록 끝, dir>0=다음 블록 시작.
   * 편집영역(.content/셀)이 없는 void 블록(구분선·이미지 등)은 건너뛴다. 이동하면 true.
   */
  function focusAdjacentBlockEdge(fromBlock, dir) {
    let b = dir < 0 ? fromBlock.previousElementSibling : fromBlock.nextElementSibling;
    while (b) {
      if (b.classList && b.classList.contains("block")) {
        const regions = b.querySelectorAll('.content[contenteditable="true"], td.lumir-cell[contenteditable="true"]');
        if (regions.length) {
          const target = dir < 0 ? regions[regions.length - 1] : regions[0];
          if (dir < 0) focusEnd(target);
          else focusStart(target);
          return true;
        }
      }
      b = dir < 0 ? b.previousElementSibling : b.nextElementSibling;
    }
    return false;
  }
  // 셀 내용 기준 캐럿이 시작/끝인지(오프셋 기반, 레이아웃 무관) — 좌우 셀 이동 판정
  function caretAtCellStart(cellTd, range) {
    const r = range.cloneRange();
    r.selectNodeContents(cellTd);
    r.setEnd(range.startContainer, range.startOffset);
    return r.toString().length === 0;
  }
  function caretAtCellEnd(cellTd, range) {
    const r = range.cloneRange();
    r.selectNodeContents(cellTd);
    r.setStart(range.endContainer, range.endOffset);
    return r.toString().length === 0;
  }
  function focusCell(table, m) { focusEnd(cellEl(table, m)); }
  function focusCellAt(table, r, c) {
    const G = table.__model.G;
    r = clamp(r, 0, G.length - 1);
    c = clamp(c, 0, G[0].length - 1);
    focusCell(table, G[r][c]);
  }
  function inGrid(m, master) {
    for (let r = 0; r < m.G.length; r++) if (m.G[r].indexOf(master) >= 0) return true;
    return false;
  }
  function getTargets(table) {
    if (selectedCells.length) return [...new Set(selectedCells)];
    if (activeCell && activeCell.closest(".lumir-tbl") === table && activeCell.__master) return [activeCell.__master];
    const td = table.querySelector("td.lumir-cell");
    return td && td.__master ? [td.__master] : [];
  }
  function selBBox(table) {
    const ts = getTargets(table);
    let r1 = 1e9, c1 = 1e9, r2 = -1, c2 = -1;
    ts.forEach((m) => {
      r1 = Math.min(r1, m.r); c1 = Math.min(c1, m.c);
      r2 = Math.max(r2, m.r + m.rowspan - 1); c2 = Math.max(c2, m.c + m.colspan - 1);
    });
    return { r1, c1, r2, c2 };
  }

  // ---------- selection ----------
  function removeSelBox(table) {
    (table || editorEl).querySelectorAll(".sel-box").forEach((b) => b.remove());
  }
  function clearSel(table) {
    (table || editorEl).querySelectorAll(".sel-cell").forEach((td) => td.classList.remove("sel-cell"));
    selectedCells = [];
    removeSelBox(table || editorEl);
  }
  function selectRange(table, aTd, bTd) {
    const m = table.__model;
    const a = aTd.__master, b = bTd.__master;
    let r1 = Math.min(a.r, b.r), c1 = Math.min(a.c, b.c);
    let r2 = Math.max(a.r + a.rowspan - 1, b.r + b.rowspan - 1), c2 = Math.max(a.c + a.colspan - 1, b.c + b.colspan - 1);
    // 병합 인지 확장
    let changed = true;
    while (changed) {
      changed = false;
      for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
        const cell = m.G[r][c];
        if (cell.r < r1) { r1 = cell.r; changed = true; }
        if (cell.c < c1) { c1 = cell.c; changed = true; }
        if (cell.r + cell.rowspan - 1 > r2) { r2 = cell.r + cell.rowspan - 1; changed = true; }
        if (cell.c + cell.colspan - 1 > c2) { c2 = cell.c + cell.colspan - 1; changed = true; }
      }
    }
    clearSel(table);
    const seen = new Set();
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
      const cell = m.G[r][c];
      if (seen.has(cell)) continue;
      seen.add(cell);
      selectedCells.push(cell);
      const td = cellEl(table, cell);
      if (td) td.classList.add("sel-cell");
    }
  }

  // ---------- structural op ----------
  function runStructural(table, fn) {
    syncModelFromTableDom(table);
    const keep = activeCell && activeCell.__master;
    fn();
    rerenderTable(table);
    if (clampTableWidth(table)) rerenderTable(table); // 부모 폭 초과 시 1회 재조정
    clearSel(table);
    const m = table.__model;
    const target = keep && inGrid(m, keep) ? keep : m.G[0] && m.G[0][0];
    if (target) focusCell(table, target);
    addResizeHandles(table);
    onChange();
    if (toolbarVisible() && activeTable === table) { showToolbar(table); updateToolbarStates(); }
  }

  // ---------- attr setters (no re-render) ----------
  function setCellAttr(table, attr, value, applyDom) {
    getTargets(table).forEach((m) => {
      if (value == null || value === "default") delete m[attr];
      else m[attr] = value;
      const td = cellEl(table, m);
      if (td) applyDom(td, value);
    });
    onChange();
  }

  // ---------- 표 전체 정렬 (F1) — 표 블록 props.tableAlignment ----------
  function setTableAlignment(table, value) {
    const block = table.closest(".block");
    if (!block) return;
    if (!block.__props) block.__props = {};
    if (value && value !== "left") { block.__props.tableAlignment = value; block.dataset.tableAlignment = value; }
    else { delete block.__props.tableAlignment; block.removeAttribute("data-table-alignment"); }
    // 정렬로 표 위치가 바뀌므로 핸들(열추가·리사이저·그립·스케일)을 새 위치로 재측정하고 툴바도 재배치.
    addResizeHandles(table);
    if (toolbarVisible() && activeTable === table) showToolbar(table);
    onChange();
  }
  function tableAlignmentOf(table) {
    const block = table.closest(".block");
    return (block && block.__props && block.__props.tableAlignment) || "left";
  }

  // ---------- toolbar ----------
  function buttn(act, title, extra) {
    // SVG 아이콘 버튼. 스크린리더용 aria-label(=title) 부여. (block-editor.html 툴바 스타일)
    return `<button type="button" class="tt-btn ${extra || ""}" data-act="${act}" title="${title}" aria-label="${title}">${SVG_ICON(TT_ICN[act] || "")}</button>`;
  }
  function buildToolbarHtml() {
    const sep = '<span class="tt-sep"></span>';
    const fill = FILL_COLORS.map((c) => c === "default"
      ? `<button type="button" class="tt-sw tt-none" data-fill="default" title="없음" aria-label="배경색 없음"></button>`
      : `<button type="button" class="tt-sw" data-fill="${c}" style="background:${HEX[c]}" title="${c}" aria-label="배경색 ${c}"></button>`).join("");
    const tcol = FILL_COLORS.map((c) => c === "default"
      ? `<button type="button" class="tt-sw tt-none" data-tcolor="default" title="기본" aria-label="글자색 기본"></button>`
      : `<button type="button" class="tt-sw" data-tcolor="${c}" style="background:${TEXT_HEX[c]}" title="${c}" aria-label="글자색 ${c}"></button>`).join("");
    const t = (k, d) => LT[k] || d;
    // 단일 행 그룹 구성(구조 → 병합 → 정렬 → 세로정렬 → 색 → 표정렬 → 머리 → 삭제).
    const rowGrp = buttn("rowAbove", t("rowAbove", "위에 행")) + buttn("rowBelow", t("rowBelow", "아래에 행")) + buttn("rowDel", t("rowDelete", "행 삭제"));
    const colGrp = buttn("colLeft", t("colLeft", "왼쪽에 열")) + buttn("colRight", t("colRight", "오른쪽에 열")) + buttn("colDel", t("colDelete", "열 삭제"));
    const mergeGrp = tbl.splitCells !== false ? buttn("merge", t("merge", "셀 병합")) + buttn("split", t("split", "병합 해제")) : "";
    const alignGrp = buttn("alignL", t("alignLeft", "왼쪽")) + buttn("alignC", t("alignCenter", "가운데")) + buttn("alignR", t("alignRight", "오른쪽"));
    const valGrp = buttn("valTop", t("vTop", "위")) + buttn("valMid", t("vMiddle", "중간")) + buttn("valBot", t("vBottom", "아래"));
    const fillGrp = tbl.cellBackgroundColor !== false ? `<div class="tt-pop-wrap">` + buttn("fillPop", t("fill", "셀 배경색")) + `<div class="tt-pop" data-pop="fill" role="menu" aria-label="${t("fill", "셀 배경색")}">${fill}</div></div>` : "";
    const tcolGrp = tbl.cellTextColor !== false ? `<div class="tt-pop-wrap">` + buttn("tcolorPop", t("textColor", "셀 글자색")) + `<div class="tt-pop" data-pop="tcolor" role="menu" aria-label="${t("textColor", "셀 글자색")}">${tcol}</div></div>` : "";
    const colorGrp = fillGrp + tcolGrp;
    const tblGrp = buttn("tblL", t("tableLeft", "표 왼쪽 정렬")) + buttn("tblC", t("tableCenter", "표 가운데 정렬")) + buttn("tblR", t("tableRight", "표 오른쪽 정렬"));
    const headerGrp = tbl.headers !== false ? buttn("headerRow", t("headerRow", "머리행")) + buttn("headerCol", t("headerCol", "머리열")) : "";
    // 셀 안 체크박스/순서·불렛 목록 삽입(줄마다 항목=병렬, CSS 자동번호). tbl.checkbox === false로 비활성.
    const insGrp = tbl.checkbox !== false
      ? buttn("insertCheckbox", t("insertCheckbox", "체크박스")) +
        buttn("orderedList", t("orderedList", "번호 목록")) +
        buttn("bulletList", t("bulletList", "글머리 목록"))
      : "";
    const groups = [rowGrp, colGrp, mergeGrp, alignGrp, valGrp, colorGrp, tblGrp, headerGrp, insGrp].filter(Boolean);
    return groups.join(sep) + sep + buttn("del", t("deleteTable", "표 삭제"), "danger");
  }
  function toolbarVisible() { return bar.style.display === "flex"; }
  function closePops() { bar.querySelectorAll(".tt-pop").forEach((p) => p.classList.remove("open")); }
  function showToolbar(table) {
    bar.style.display = "flex";
    const r = table.getBoundingClientRect ? table.getBoundingClientRect() : { top: 0, left: 0 };
    // 좁은 화면: max-width로 감싼 툴바가 뷰포트 우측을 넘지 않도록 left 클램프(줄바꿈은 CSS wrap이 처리).
    const bw = bar.offsetWidth || 0;
    const vw = (document.documentElement && document.documentElement.clientWidth) || window.innerWidth || 0;
    let left = r.left || 0;
    if (vw && bw && left + bw > vw - 8) left = vw - bw - 8;
    if (left < 8) left = 8;
    bar.style.top = Math.max(window.scrollY + 4, window.scrollY + r.top - (bar.offsetHeight || 0) - 8) + "px";
    bar.style.left = window.scrollX + left + "px";
  }
  function hideToolbar() { bar.style.display = "none"; closePops(); }
  function setOn(act, on) { const el = bar.querySelector(`[data-act="${act}"]`); if (el) el.classList.toggle("on", !!on); }
  function updateToolbarStates() {
    if (!activeTable) return;
    const o = activeTable.__model;
    setOn("headerRow", (o.headerRows || 0) > 0);
    setOn("headerCol", (o.headerCols || 0) > 0);
    const m = activeCell && activeCell.__master;
    setOn("alignL", m && m.textAlignment === "left");
    setOn("alignC", m && m.textAlignment === "center");
    setOn("alignR", m && m.textAlignment === "right");
    setOn("valTop", m && (!m.verticalAlignment || m.verticalAlignment === "top"));
    setOn("valMid", m && m.verticalAlignment === "middle");
    setOn("valBot", m && m.verticalAlignment === "bottom");
    const ta = tableAlignmentOf(activeTable);
    setOn("tblL", ta === "left");
    setOn("tblC", ta === "center");
    setOn("tblR", ta === "right");
    // 표가 최대폭이면 정렬은 의미가 없고 핸들 영역을 침범하므로 정렬 버튼 비활성화.
    const full = tableAtMaxWidth(activeTable);
    ["tblL", "tblC", "tblR"].forEach((a) => { const el = bar.querySelector(`[data-act="${a}"]`); if (el) el.disabled = full; });
    // 열 추가 여유 없음(물리 하한 도달) → 열 추가 버튼 명시적 비활성(무반응 대신 disabled+커서)
    const noRoom = !canAddColumn(activeTable);
    ["colLeft", "colRight"].forEach((a) => { const el = bar.querySelector(`[data-act="${a}"]`); if (el) el.disabled = noRoom; });
  }

  bar.addEventListener("mousedown", (e) => { if (!e.target.closest("input,select")) e.preventDefault(); });
  bar.addEventListener("click", (e) => {
    const t = activeTable;
    if (!t) return;
    const sw = e.target.closest(".tt-sw");
    if (sw) {
      if (sw.dataset.fill != null) setCellAttr(t, "backgroundColor", sw.dataset.fill, (td, v) => { if (v && v !== "default") td.dataset.backgroundColor = v; else delete td.dataset.backgroundColor; });
      else if (sw.dataset.tcolor != null) setCellAttr(t, "textColor", sw.dataset.tcolor, (td, v) => { if (v && v !== "default") td.dataset.textColor = v; else delete td.dataset.textColor; });
      closePops(); return;
    }
    const b = e.target.closest(".tt-btn");
    if (!b) return;
    const act = b.dataset.act;
    const bb = () => selBBox(t);
    switch (act) {
      case "rowAbove": runStructural(t, () => insertRow(t.__model, bb().r1)); break;
      case "rowBelow": runStructural(t, () => insertRow(t.__model, bb().r2 + 1)); break;
      case "rowDel": { const x = bb(); runStructural(t, () => deleteRows(t.__model, x.r1, x.r2)); break; }
      case "colLeft": if (canAddColumn(t)) runStructural(t, () => insertCol(t.__model, bb().c1)); break;
      case "colRight": if (canAddColumn(t)) runStructural(t, () => insertCol(t.__model, bb().c2 + 1)); break;
      case "colDel": { const x = bb(); runStructural(t, () => deleteCols(t.__model, x.c1, x.c2)); break; }
      case "merge": runStructural(t, () => mergeCells(t.__model, bb())); break;
      case "split": { const m = activeCell && activeCell.__master; runStructural(t, () => splitCell(t.__model, m)); break; }
      case "alignL": setCellAttr(t, "textAlignment", "left", (td, v) => (td.style.textAlign = v)); break;
      case "alignC": setCellAttr(t, "textAlignment", "center", (td, v) => (td.style.textAlign = v)); break;
      case "alignR": setCellAttr(t, "textAlignment", "right", (td, v) => (td.style.textAlign = v)); break;
      case "valTop": setCellAttr(t, "verticalAlignment", "default", (td) => (td.style.verticalAlign = "")); break;
      case "valMid": setCellAttr(t, "verticalAlignment", "middle", (td, v) => (td.style.verticalAlign = v)); break;
      case "valBot": setCellAttr(t, "verticalAlignment", "bottom", (td, v) => (td.style.verticalAlign = v)); break;
      case "fillPop": togglePop("fill"); break;
      case "tcolorPop": togglePop("tcolor"); break;
      case "tblL": setTableAlignment(t, "left"); break;
      case "tblC": setTableAlignment(t, "center"); break;
      case "tblR": setTableAlignment(t, "right"); break;
      case "headerRow": case "headerCol": {
        syncModelFromTableDom(t);
        const keepH = activeCell && activeCell.__master;
        if (act === "headerRow") t.__model.headerRows = (t.__model.headerRows ? 0 : 1);
        else t.__model.headerCols = (t.__model.headerCols ? 0 : 1);
        rerenderTable(t);
        // 재렌더로 셀 DOM이 교체되므로 활성 셀을 다시 포커스한다. 안 하면 activeCell이 detached 노드를
        // 가리켜, 이후 색/정렬 등 속성 조작이 getTargets 폴백으로 첫 셀에 잘못 적용된다(캐럿도 유실).
        // 헤더 토글은 구조를 바꾸지 않으므로 master 참조는 그대로 유효하다.
        if (keepH && inGrid(t.__model, keepH)) focusCell(t, keepH);
        addResizeHandles(t); onChange(); updateToolbarStates(); break;
      }
      case "insertCheckbox": insertCheckbox(t); break;
      case "orderedList": insertListMarker(t, "ol"); break;
      case "bulletList": insertListMarker(t, "ul"); break;
      case "del": deleteTable(t); return;
    }
    updateToolbarStates();
  });
  function togglePop(which) {
    const p = bar.querySelector(`.tt-pop[data-pop="${which}"]`);
    const open = p.classList.contains("open");
    closePops();
    if (!open) p.classList.add("open");
  }
  function deleteTable(table) {
    const block = table.closest(".block");
    hideToolbar();
    activeTable = null; activeCell = null;
    if (block) block.remove();
    onChange();
  }

  // ---------- cell checkbox (인라인 원자: 직렬=연속 삽입, 병렬=Enter 줄바꿈) ----------
  function isReadonly() { return editorEl.classList && editorEl.classList.contains("lumir-readonly"); }
  function makeCheckboxEl(checked) {
    const span = document.createElement("span");
    span.className = "lumir-cell-check" + (checked ? " checked" : "");
    span.setAttribute("role", "checkbox");
    span.setAttribute("aria-checked", checked ? "true" : "false");
    span.setAttribute("contenteditable", "false");
    span.setAttribute("data-lumir-checkbox", "1");
    span.setAttribute("data-checked", checked ? "true" : "false");
    return span;
  }
  /** 활성 셀의 캐럿 위치에 체크박스 삽입(캐럿이 셀 밖이면 셀 끝에 추가) → 모델 회수 + 변경 알림. */
  function insertCheckbox(table) {
    if (isReadonly()) return;
    const td = activeCell && activeCell.closest && activeCell.closest("td.lumir-cell");
    if (!td || td.closest(".lumir-tbl") !== table) return;
    const span = makeCheckboxEl(false);
    const sel = typeof window !== "undefined" && window.getSelection ? window.getSelection() : null;
    let r = null;
    if (sel && sel.rangeCount) {
      const cand = sel.getRangeAt(0);
      if (td.contains(cand.commonAncestorContainer)) r = cand;
    }
    if (r) {
      r.deleteContents();
      r.insertNode(span);
    } else {
      td.appendChild(span);
      r = document.createRange();
    }
    // contenteditable=false 원자 뒤에 캐럿이 놓이도록 보조: 다음 형제가 없으면 ZWSP(직렬화에서 제거) 앵커 추가.
    if (!span.nextSibling) span.after(document.createTextNode("​"));
    try {
      r.setStartAfter(span);
      r.collapse(true);
      if (sel) { sel.removeAllRanges(); sel.addRange(r); }
    } catch (_) { /* jsdom 등 selection 미구현 환경 무시 */ }
    syncModelFromTableDom(table);
    onChange();
    refreshActiveTableOverlays(); // 삽입으로 셀/표 크기가 바뀔 수 있으므로 오버레이 재배치
  }

  // ---------- cell ordered/bullet list (인라인 마커 원자, 줄 시작 = 항목, 병렬=<br>, 번호=CSS 카운터) ----------
  function isHardBrEl(n) {
    return n && n.nodeType === 1 && n.tagName === "BR" && !(n.hasAttribute && n.hasAttribute("data-lumir-br-filler"));
  }
  /** 인라인 원자(체크박스/순서·불렛 마커) 요소인지 — Backspace 삭제 판정용. */
  function isAtomEl(n) {
    return n && n.nodeType === 1 && n.hasAttribute &&
      (n.hasAttribute("data-lumir-checkbox") || n.hasAttribute("data-lumir-ol") || n.hasAttribute("data-lumir-ul"));
  }
  function makeListEl(kind) {
    const span = document.createElement("span");
    span.className = kind === "ol" ? "lumir-cell-ol" : "lumir-cell-ul";
    span.setAttribute("contenteditable", "false");
    span.setAttribute(kind === "ol" ? "data-lumir-ol" : "data-lumir-ul", "1");
    return span;
  }
  /** 캐럿이 속한 라인(=<br>로 구분)의 td 직속 child 인덱스 범위·선두 마커·내용유무 분석. */
  function currentLineInfo(td) {
    const sel = typeof window !== "undefined" && window.getSelection ? window.getSelection() : null;
    if (!sel || !sel.rangeCount) return null;
    const r = sel.getRangeAt(0);
    if (!td.contains(r.startContainer)) return null;
    const kids = td.childNodes;
    // 캐럿의 td 직속 조상 child 위치(caretIdx: 그 child 인덱스; td 직접이면 startOffset)
    let caretIdx;
    if (r.startContainer === td) caretIdx = r.startOffset;
    else {
      let top = r.startContainer;
      while (top && top.parentNode !== td) top = top.parentNode;
      if (!top) return null;
      caretIdx = Array.prototype.indexOf.call(kids, top);
    }
    let s = caretIdx - 1;
    while (s >= 0 && !isHardBrEl(kids[s])) s--;
    const lineStart = s + 1; // <br> 다음(또는 0)
    let e = lineStart;
    while (e < kids.length && !isHardBrEl(kids[e])) e++;
    const lineEnd = e; // 다음 <br> 인덱스 또는 length(배타)
    const first = kids[lineStart];
    let markerEl = null, kind = null;
    if (first && first.nodeType === 1 && first.hasAttribute) {
      if (first.hasAttribute("data-lumir-ol")) { markerEl = first; kind = "ol"; }
      else if (first.hasAttribute("data-lumir-ul")) { markerEl = first; kind = "ul"; }
    }
    let hasContent = false;
    for (let i = lineStart + (markerEl ? 1 : 0); i < lineEnd; i++) {
      const k = kids[i];
      if (k.nodeType === 3) { if (k.nodeValue.replace(/​/g, "").trim().length) { hasContent = true; break; } }
      else if (k.nodeType === 1 && k.tagName !== "BR") { hasContent = true; break; }
    }
    return { markerEl, kind, hasContent, lineStart, lineEnd };
  }
  /** 활성 셀의 현재 라인 시작에 순서/불렛 마커 삽입(이미 마커 있으면 종류 교체) → 회수/알림/재배치. */
  function insertListMarker(table, kind) {
    if (isReadonly()) return;
    const td = activeCell && activeCell.closest && activeCell.closest("td.lumir-cell");
    if (!td || td.closest(".lumir-tbl") !== table) return;
    const info = currentLineInfo(td);
    const marker = makeListEl(kind);
    if (info && info.markerEl) {
      info.markerEl.replaceWith(marker); // 종류 전환(ol↔ul)
    } else {
      const at = info ? info.lineStart : td.childNodes.length;
      const ref = td.childNodes[at] || null;
      td.insertBefore(marker, ref);
    }
    if (!marker.nextSibling) marker.after(document.createTextNode("​"));
    try {
      const nr = document.createRange();
      nr.setStartAfter(marker);
      nr.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(nr);
    } catch (_) { /* selection 미구현 환경 무시 */ }
    syncModelFromTableDom(table);
    onChange();
    refreshActiveTableOverlays();
  }
  /**
   * 셀 자동서식: 라인 시작의 "N."(→순서) / "-"·"*"(→불렛) + 스페이스 → 리스트 마커.
   * 라인 시작(셀 첫머리 또는 <br> 직후)에서만. 성공 시 true(스페이스 취소).
   */
  function tryCellListAutoformat(td) {
    if (tbl.checkbox === false || isReadonly()) return false; // (list도 checkbox 토글과 동일 config로 on/off)
    const sel = typeof window !== "undefined" && window.getSelection ? window.getSelection() : null;
    if (!sel || !sel.rangeCount || !sel.isCollapsed) return false;
    const r = sel.getRangeAt(0);
    const node = r.startContainer;
    if (!node || node.nodeType !== 3 || !td.contains(node)) return false;
    const before = node.nodeValue.slice(0, r.startOffset);
    let kind = null;
    if (/^\d+\.$/.test(before)) kind = "ol";
    else if (/^[-*]$/.test(before)) kind = "ul";
    if (!kind) return false;
    // 라인 시작 판정: 직전 형제(빈 텍스트 제외)가 없거나 <br>
    let prev = node.previousSibling;
    while (prev && prev.nodeType === 3 && prev.nodeValue === "") prev = prev.previousSibling;
    const atLineStart = !prev || isHardBrEl(prev);
    if (!atLineStart) return false;
    // 마커 텍스트 제거 + 마커 삽입
    const marker = makeListEl(kind);
    const parent = node.parentNode;
    const tail = node.nodeValue.slice(r.startOffset);
    parent.insertBefore(marker, node);
    if (tail) node.nodeValue = tail; else parent.removeChild(node);
    if (!marker.nextSibling) marker.after(document.createTextNode("​"));
    try {
      const nr = document.createRange();
      nr.setStartAfter(marker);
      nr.collapse(true);
      sel.removeAllRanges();
      sel.addRange(nr);
    } catch (_) { /* ignore */ }
    syncModelFromTableDom(td.closest(".lumir-tbl"));
    onChange();
    refreshActiveTableOverlays();
    return true;
  }
  /**
   * 셀 안 마크다운 자동서식: 캐럿 바로 앞의 "[]" 또는 "[ ]" + 스페이스 → 체크박스로 변환.
   * (블록 레벨 editing.js MD_RULES의 checkListItem 규칙을 셀 인라인용으로 이식하되, 셀에서는
   *  줄 시작뿐 아니라 캐럿 어디서나 변환한다 → 한 줄에 연속 입력 시 직렬(나란히) 배치가 된다.
   *  병렬은 Enter로 줄을 바꾼 뒤 반복.) 성공 시 true(스페이스 입력 취소).
   */
  function tryCellCheckboxAutoformat(td) {
    if (tbl.checkbox === false || isReadonly()) return false;
    const sel = typeof window !== "undefined" && window.getSelection ? window.getSelection() : null;
    if (!sel || !sel.rangeCount || !sel.isCollapsed) return false;
    const r = sel.getRangeAt(0);
    const node = r.startContainer;
    if (!node || node.nodeType !== 3 || !td.contains(node)) return false; // 텍스트 노드 캐럿만
    const offset = r.startOffset;
    const before = node.nodeValue.slice(0, offset);
    const m = before.match(/\[\s?\]$/); // 캐럿 직전이 마커여야(위치 무관 → 직렬 지원)
    if (!m) return false;
    const markerLen = m[0].length;
    // 마커 텍스트만 제거하고 그 자리에 체크박스 삽입(앞/뒤 텍스트 보존).
    const span = makeCheckboxEl(false);
    const parent = node.parentNode;
    const head = node.nodeValue.slice(0, offset - markerLen);
    const tail = node.nodeValue.slice(offset);
    node.nodeValue = head + tail;
    const after = node.splitText(head.length); // after = 마커 뒤 텍스트 노드(빈 노드일 수 있음)
    parent.insertBefore(span, after);
    if (after.nodeValue === "") parent.removeChild(after);
    if (node.nodeValue === "") parent.removeChild(node);
    if (!span.nextSibling) span.after(document.createTextNode("​")); // 캐럿 앵커(ZWSP, 직렬화 제거)
    try {
      const nr = document.createRange();
      nr.setStartAfter(span);
      nr.collapse(true);
      sel.removeAllRanges();
      sel.addRange(nr);
    } catch (_) { /* selection 미구현 환경 무시 */ }
    syncModelFromTableDom(td.closest(".lumir-tbl"));
    onChange();
    refreshActiveTableOverlays(); // 자동서식 삽입 후 오버레이 재배치
    return true;
  }
  /** 체크박스 클릭 토글 — 상태 속성만 변경(재렌더 없음, 캐럿 유지) 후 모델 회수 + 변경 알림. */
  function toggleCellCheckbox(box, e) {
    const td = box.closest && box.closest("td.lumir-cell");
    if (!td) return;
    const table = td.closest(".lumir-tbl");
    if (!table || !editorEl.contains(table)) return;
    e.preventDefault(); // 캐럿 클릭 이동 방지(체크만 토글)
    if (isReadonly()) return;
    const now = box.getAttribute("data-checked") !== "true";
    box.setAttribute("data-checked", now ? "true" : "false");
    box.setAttribute("aria-checked", now ? "true" : "false");
    box.classList.toggle("checked", now);
    syncModelFromTableDom(table);
    onChange();
  }

  // ---------- resize handles + add bars ----------
  function addResizeHandles(table) {
    const host = table.parentElement; // .content (table 직속 부모)
    if (!host) return;
    host.querySelectorAll(".tbl-handles").forEach((h) => h.remove());
    const cont = document.createElement("div");
    cont.className = "tbl-handles";
    cont.setAttribute("contenteditable", "false");
    const cols = table.__model.G[0] ? table.__model.G[0].length : 0;
    const rows = table.__model.G.length;

    // 셀 경계 측정(브라우저). jsdom에선 0 → 핸들은 생성되되 위치 0(테스트 무관).
    const hostRect = host.getBoundingClientRect ? host.getBoundingClientRect() : { left: 0, top: 0 };
    const tableRect = table.getBoundingClientRect ? table.getBoundingClientRect() : { left: 0, top: 0, width: 0, height: 0 };
    // 표의 콘텐츠 내 좌/상 오프셋 — 표 정렬(margin:auto)로 표가 우측으로 밀리면 L>0.
    // 핸들 컨테이너를 표 박스에 정확히 오버레이하고, 셀 좌표를 "표 좌상단(0,0) 기준" 로컬 좌표로
    // 측정한다. 이러면 좌측 기준 핸들(행리사이저·행추가바·하변스케일·열그립·행그립)이 정렬과
    // 무관하게 표에 정렬되어, 정렬 시 표 왼쪽 거터로 삐져나오던 겹침이 사라진다.
    const L = (tableRect.left || 0) - (hostRect.left || 0);
    const T = (tableRect.top || 0) - (hostRect.top || 0);
    const right = new Array(cols).fill(0), bottom = new Array(rows).fill(0);
    table.querySelectorAll("td.lumir-cell").forEach((td) => {
      const m = td.__master;
      const rb = td.getBoundingClientRect ? td.getBoundingClientRect() : { right: 0, bottom: 0 };
      const x = rb.right - (tableRect.left || 0), y = rb.bottom - (tableRect.top || 0);
      const ck = m.c + m.colspan - 1, rk = m.r + m.rowspan - 1;
      if (x > right[ck]) right[ck] = x;
      if (y > bottom[rk]) bottom[rk] = y;
    });
    const H = tableRect.height || 0;
    const W = right[cols - 1] || tableRect.width || 0;
    // 컨테이너를 표 박스에 오버레이(CSS inset:0 해제). 이후 모든 핸들 좌표는 표-로컬.
    cont.style.left = L + "px"; cont.style.top = T + "px";
    cont.style.width = W + "px"; cont.style.height = H + "px";
    cont.style.right = "auto"; cont.style.bottom = "auto";

    const bottomY = bottom[rows - 1] || H;
    // 열/행 좌상단 경계 좌표(세그먼트 분할·핸들 배치 공용).
    const G = table.__model.G;
    const lefts = []; { let prev = 0; for (let c = 0; c < cols; c++) { lefts.push(prev); prev = right[c]; } }
    const tops = []; { let prev = 0; for (let r = 0; r < rows; r++) { tops.push(prev); prev = bottom[r]; } }
    // 내부 열/행 리사이저 — 경계가 "실제로 존재하는" 구간에만 생성한다.
    //   병합 셀(colspan/rowspan)이 경계를 가로지르는 칸에는 내부 보더가 없으므로 리사이저(hit + hover 보더 바)를
    //   만들지 않는다. 병합 셀 내부에 리사이즈 커서·파란 라인이 뜨던 문제 방지. 경계 존재 = 양쪽 슬롯 master 참조가 다름.
    // col-resizer(열 경계 k): 세로 세그먼트(행 방향). G[r][k] === G[r][k+1](가로 병합) 행은 제외.
    for (let k = 0; k < cols - 1; k++) {
      let r = 0;
      while (r < rows) {
        if (G[r][k] === G[r][k + 1]) { r++; continue; }
        const y0 = tops[r];
        while (r < rows && G[r][k] !== G[r][k + 1]) r++;
        const seg = document.createElement("div");
        seg.className = "col-resizer"; seg.dataset.col = String(k);
        seg.style.left = right[k] + "px"; seg.style.top = y0 + "px"; seg.style.height = (bottom[r - 1] - y0) + "px";
        seg.innerHTML = '<div class="bar"></div>';
        cont.appendChild(seg);
      }
    }
    // row-resizer(행 경계 k): 가로 세그먼트(열 방향). G[k][c] === G[k+1][c](세로 병합) 열은 제외.
    for (let k = 0; k < rows - 1; k++) {
      let c = 0;
      while (c < cols) {
        if (G[k][c] === G[k + 1][c]) { c++; continue; }
        const x0 = lefts[c];
        while (c < cols && G[k][c] !== G[k + 1][c]) c++;
        const seg = document.createElement("div");
        seg.className = "row-resizer"; seg.dataset.row = String(k);
        seg.style.top = bottom[k] + "px"; seg.style.left = x0 + "px"; seg.style.width = (right[c - 1] - x0) + "px";
        seg.innerHTML = '<div class="bar"></div>';
        cont.appendChild(seg);
      }
    }
    // 행/열 추가 버튼(hover-reveal): addrow=하변 전체, addcol=우측 거터, addboth=우하단 (block-editor.html UI)
    const hostW = host.clientWidth || 0;
    let addColLeft = W + 6;
    if (hostW && L + addColLeft + 16 > hostW) addColLeft = Math.max(W + 2, hostW - L - 16);
    const addrow = document.createElement("div");
    addrow.className = "tbl-add tbl-addrow"; addrow.title = "행 추가"; addrow.innerHTML = "<span>+</span>";
    addrow.style.top = bottomY + 4 + "px"; addrow.style.width = W + "px";
    cont.appendChild(addrow);
    const addcol = document.createElement("div");
    addcol.className = "tbl-add tbl-addcol"; addcol.title = "열 추가"; addcol.innerHTML = "<span>+</span>";
    addcol.style.left = addColLeft + "px"; addcol.style.height = bottomY + "px"; // 표 전체 높이(addboth는 표 아래라 비겹침)
    cont.appendChild(addcol);
    const addboth = document.createElement("div");
    addboth.className = "tbl-add tbl-addboth"; addboth.title = "행·열 추가"; addboth.innerHTML = "<span>+</span>";
    addboth.style.left = addColLeft + "px"; addboth.style.top = bottomY + 4 + "px";
    cont.appendChild(addboth);

    // 전체 스케일 핸들 — 마지막 열 우변(열 전체 스케일)·마지막 행 하변(행 전체 스케일)·코너(양축). (block-editor.html 모델)
    const edgeR = document.createElement("div");
    edgeR.className = "edge-resizer edge-right"; edgeR.style.left = W + "px"; edgeR.style.height = bottomY + "px";
    edgeR.title = "열 너비 일괄 조절"; edgeR.innerHTML = '<div class="bar"></div>';
    cont.appendChild(edgeR);
    const edgeB = document.createElement("div");
    edgeB.className = "edge-resizer edge-bottom"; edgeB.style.top = bottomY + "px"; edgeB.style.width = W + "px";
    edgeB.title = "행 높이 일괄 조절"; edgeB.innerHTML = '<div class="bar"></div>';
    cont.appendChild(edgeB);
    const corner = document.createElement("div");
    corner.className = "edge-resizer edge-corner"; corner.style.left = W + "px"; corner.style.top = bottomY + "px";
    corner.title = "표 크기 조절"; corner.innerHTML = '<div class="bar"></div>';
    cont.appendChild(corner);

    // 행/열 이동 그립(⠿) 제거 — 양식은 구조 고정이라 드래그 재정렬 불필요. 좌/상단 거터를 차지하지 않는다.
    // (재정렬이 필요하면 표 툴바의 이동 버튼으로 대체 가능 — ops.js moveRow/moveCol 재사용.)

    host.appendChild(cont);
  }
  // 열 우변 경계(px, 표 좌상단 기준). cell rect 기반 — colspan/병합 셀은 마지막 열(c+colspan-1)에만
  // 우변을 기록하므로, 개별 셀 폭/colspan 균등분할로 열폭을 추정하던 오차를 없앤다(addResizeHandles right[]와 동일 방식).
  function measureColRights(table) {
    const G = table.__model.G || [];
    const cols = G[0] ? G[0].length : 0;
    const tableRect = table.getBoundingClientRect ? table.getBoundingClientRect() : { left: 0 };
    const right = new Array(cols).fill(0);
    table.querySelectorAll("td.lumir-cell").forEach((td) => {
      const m = td.__master;
      const rb = td.getBoundingClientRect ? td.getBoundingClientRect() : { right: 0 };
      const x = rb.right - (tableRect.left || 0);
      const ck = m.c + m.colspan - 1;
      if (x > right[ck]) right[ck] = x;
    });
    return right;
  }
  function startColResize(rz, e) {
    const table = rz.closest(".content").querySelector(".lumir-tbl");
    const m = table.__model;
    const k = +rz.dataset.col;
    const cols = (m.G[0] || []).length;
    const colsEl = table.querySelectorAll("colgroup col");
    // 리사이즈 시작 시 모든 열 너비를 "고정"한다(null 자동 재분배 차단).
    // 모델 columnWidths가 이미 유효하면 그대로 사용한다 — 재측정(curColWidths)은 병합열 폴백·colspan
    // 균등분할·반올림·border-collapse 공유보더 때문에 실제 표폭과 어긋나, 리사이저를 "잡는 순간"
    // 표 전체 폭이 튀는 원인이었다. auto(null)/미설정 표만 측정으로 폴백.
    const modelValid = Array.isArray(m.columnWidths) && m.columnWidths.length === cols && m.columnWidths.every((w) => w > 0);
    const frozen = modelValid ? m.columnWidths.slice() : curColWidths(table);
    m.columnWidths = frozen.slice();
    frozen.forEach((w, i) => { if (colsEl[i]) colsEl[i].style.width = w + "px"; });
    // 드래그 중엔 포인터가 얇은 리사이저 띠 밖으로 나가 :hover가 풀린다 → body 커서를 고정하고
    // .resizing 클래스로 경계 하이라이트(.bar)를 유지한다(스케일 핸들과 동일한 UX).
    document.body.style.cursor = "col-resize";
    rz.classList.add("resizing");
    // 경계 k의 리사이저 세그먼트(병합으로 분할됐을 수 있음) — 드래그 중 하이라이트가 이동하는 경계를 따라가도록 함께 이동.
    const cont = rz.closest(".tbl-handles");
    const segs = cont ? [...cont.querySelectorAll(`.col-resizer[data-col="${k}"]`)] : [rz];
    const segLeft0 = segs.map((s) => parseFloat(s.style.left) || 0);
    const startX = e.clientX;
    const j = k + 1; // 오른쪽 인접 열
    const startWk = frozen[k];
    const startWj = frozen[j] != null ? frozen[j] : startWk;
    // 내부 경계 재분배: 경계를 δ만큼 옮기면 k는 +δ, j는 −δ (전체 표폭 = 상수 → 정렬·위치 불변, no-scroll 정합).
    // 마지막 열 우변은 리사이저가 아니라 edge-right(열 전체 스케일)가 담당한다. δ는 두 열 모두 fMin 이상 클램프.
    const fMin = effMinCol(cols, maxTableWidth(table), MIN_COL); // compact 표는 40 미만까지 허용(예산 미상이면 40)
    const minDelta = fMin - startWk;
    const maxDelta = startWj - fMin;
    function move(ev) {
      let d = Math.round(ev.clientX - startX);
      if (d < minDelta) d = minDelta;
      if (d > maxDelta) d = maxDelta;
      const wk = startWk + d, wj = startWj - d;
      m.columnWidths[k] = wk;
      m.columnWidths[j] = wj;
      if (colsEl[k]) colsEl[k].style.width = wk + "px";
      if (colsEl[j]) colsEl[j].style.width = wj + "px";
      // 열폭이 fMin(<40 가능)까지 내려가면 CSS min-width(40)가 렌더를 40으로 되밀어 표폭이 벌어진다.
      // 스케일 경로(applyColWidthsLive)와 동일하게 compact 클래스를 실시간 토글해 min-width를 24로 낮춘다(모델=렌더 정합).
      syncCompact(table);
      // 경계는 열 k의 우변 → 폭 변화량 d 만큼 우측 이동. 하이라이트 세그먼트를 새 경계로 옮긴다.
      segs.forEach((s, i) => { s.style.left = segLeft0[i] + d + "px"; });
    }
    function up() {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.style.cursor = "";
      rz.classList.remove("resizing");
      addResizeHandles(table); // 경계가 이동했으므로 핸들 위치 갱신
      onChange();
    }
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  }
  function startRowResize(rz, e) {
    const table = rz.closest(".content").querySelector(".lumir-tbl");
    const m = table.__model;
    const k = +rz.dataset.row;
    const startY = e.clientY;
    const trs = table.querySelectorAll("tbody tr");
    const tr = trs[k];
    const startH = (tr && tr.getBoundingClientRect ? Math.round(tr.getBoundingClientRect().height) : 0) || MIN_ROW;
    // 드래그 중 :hover 상실에 대비해 body 커서 고정 + .resizing 하이라이트 유지(열 리사이즈와 동일 UX).
    document.body.style.cursor = "row-resize";
    rz.classList.add("resizing");
    // 경계 k의 리사이저 세그먼트(세로 병합으로 분할됐을 수 있음) — 드래그 중 하이라이트가 이동하는 경계를 따라가도록 함께 이동.
    const cont = rz.closest(".tbl-handles");
    const segs = cont ? [...cont.querySelectorAll(`.row-resizer[data-row="${k}"]`)] : [rz];
    const segTop0 = segs.map((s) => parseFloat(s.style.top) || 0);
    function move(ev) {
      const h = Math.max(MIN_ROW, Math.round(startH + (ev.clientY - startY)));
      if (tr) tr.style.height = h + "px";
      // 행 k에서 시작하는 master에 rowHeight 저장
      for (let c = 0; c < m.G[k].length; c++) { const cell = m.G[k][c]; if (cell.r === k) cell.rowHeight = h; }
      // 하이라이트는 "실제 렌더된 행 하변"으로 이동한다. tr의 height는 최소높이로 동작하므로, 셀 콘텐츠·패딩이
      // 강제하는 렌더 최소높이(> MIN_ROW)에서는 tr이 더 줄지 않는다 → 실측 하변에 맞추면 보더·하이라이트가
      // 그 지점에서 함께 멈춘다(모델 h로만 계산하면 최소 이하로 더 올라가 실제 보더와 어긋남).
      const tRect = table.getBoundingClientRect ? table.getBoundingClientRect() : null;
      const trRect = tr && tr.getBoundingClientRect ? tr.getBoundingClientRect() : null;
      const realBottom = tRect && trRect ? trRect.bottom - tRect.top : segTop0[0] + (h - startH);
      segs.forEach((s) => { s.style.top = realBottom + "px"; });
    }
    function up() { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); document.body.style.cursor = ""; rz.classList.remove("resizing"); addResizeHandles(table); onChange(); }
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  }

  // ---------- 전체/코너 스케일 (샘플 startColScaleAll/startRowScaleAll/startCornerScale 참고) ----------
  function curColWidths(table) {
    const rights = measureColRights(table);
    const out = [];
    let prev = 0;
    for (let c = 0; c < rights.length; c++) {
      const edge = rights[c] || prev;      // 경계 미측정(전 행이 병합으로 이 열을 가로지름·jsdom 0 등)이면 폭 0 취급
      const w = Math.round(edge - prev);
      prev = edge;
      out.push(w > 0 ? w : 80);            // 측정 불가 열만 80 폴백(경계가 실재하지 않는 예외 케이스)
    }
    return out;
  }
  function curRowHeights(table) {
    return [...table.querySelectorAll("tbody tr")].map((tr) => (tr.getBoundingClientRect ? Math.round(tr.getBoundingClientRect().height) : MIN_ROW) || MIN_ROW);
  }
  function applyColWidthsLive(table, widths) {
    table.__model.columnWidths = widths.slice();
    const cols = table.querySelectorAll("colgroup col");
    widths.forEach((w, k) => { if (cols[k]) cols[k].style.width = w + "px"; });
    syncCompact(table); // 스케일 중 실시간 compact 토글(CSS min-width 정합)
  }
  function applyRowHeightsLive(table, heights) {
    const m = table.__model;
    const trs = table.querySelectorAll("tbody tr");
    heights.forEach((h, r) => {
      if (trs[r]) trs[r].style.height = h + "px";
      if (m.G[r]) for (let c = 0; c < m.G[r].length; c++) { const cell = m.G[r][c]; if (cell && cell.r === r) cell.rowHeight = h; }
    });
  }
  function liveAfter(table) { addResizeHandles(table); }
  // ---------- 부모 너비 클램프 (열 추가/스케일이 에디터 폭을 넘지 않도록) ----------
  function maxTableWidth(table) {
    // 모바일: 폭 예산 0(=미상) → clampTableWidth·canAddColumn·스케일이 모두 폭 제한을 건너뛰어
    // 표가 편집영역보다 넓으면 CSS(overflow-x)로 가로 스크롤. (데스크톱은 기존 fit-to-width 유지)
    if (isMobileViewport()) return 0;
    const host = table.closest(".content") || table.parentElement;
    const w = host && host.clientWidth ? host.clientWidth : 0;
    // .content 좌우 padding 0 → 콘텐츠박스 폭 = clientWidth. 여기까지 채우면 표가 좌·우변 콘텐츠 끝선에 flush
    // (fillInitialTablesToWidth 와 동일 기준 → 초기 채움/스케일 상한 정합). 열추가(+)는 hover-reveal 오버레이라 폭 미점유.
    return w ? Math.max(MIN_COL * 2, w) : 0;
  }
  function tableAtMaxWidth(table) {
    const maxW = maxTableWidth(table);
    if (!maxW) return false;
    const w = table.getBoundingClientRect ? table.getBoundingClientRect().width : 0;
    return w >= maxW - 4; // 표 폭이 부모 가용폭에 근접 → 정렬 무의미
  }
  function canAddColumn(table) {
    const maxW = maxTableWidth(table);
    if (!maxW) return true; // 측정 불가(jsdom/서버) → 허용
    const cols = (table.__model.G[0] || []).length;
    return (cols + 1) * HARD_MIN_COL <= maxW; // 물리 하한까지 열 추가 허용(추가 후 fit이 비례 축소)
  }
  // compact(열 다수로 40px 미만) 상태를 표 클래스에 반영 → CSS min-width가 함께 낮아짐(모델=렌더 정합).
  function syncCompact(table) {
    const cw = (table.__model && table.__model.columnWidths) || [];
    table.classList.toggle("lumir-tbl--compact", cw.some((w) => w && w < MIN_COL));
  }
  function clampWidths(widths, maxW) {
    return fitWidths(widths, maxW, MIN_COL).widths;
  }
  /** 렌더 후 호출: 표가 부모 폭을 넘으면 columnWidths를 비례 축소(변경 시 true) */
  function clampTableWidth(table) {
    const maxW = maxTableWidth(table);
    if (!maxW) return false;
    const m = table.__model;
    const cols = (m.G[0] || []).length;
    if (!cols) return false;
    const cur = (m.columnWidths && m.columnWidths.length === cols && m.columnWidths.every((w) => w)) ? m.columnWidths.slice() : curColWidths(table);
    const next = clampWidths(cur, maxW);
    if (next.some((w, i) => w !== (m.columnWidths && m.columnWidths[i]))) { m.columnWidths = next; return true; }
    return false;
  }
  function scaleDrag(table, onMove) {
    function move(ev) { onMove(ev); liveAfter(table); }
    function up() { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); document.body.style.cursor = ""; onChange(); }
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  }
  // 마지막 열 우변 = 열 전체 비례 스케일 (block-editor.html startColScaleAll). fit 정책상 clampWidths로 budget 클램프.
  function startColScaleAll(rz, e) {
    const table = rz.closest(".content").querySelector(".lumir-tbl");
    const c0 = curColWidths(table), W0 = c0.reduce((a, b) => a + b, 0) || 1, sx = e.clientX;
    document.body.style.cursor = "ew-resize";
    const maxW = maxTableWidth(table) || Infinity;
    scaleDrag(table, (ev) => { const f = Math.max(0.1, (W0 + (ev.clientX - sx)) / W0); applyColWidthsLive(table, clampWidths(c0.map((w) => Math.max(MIN_SCALE_COL, Math.round(w * f))), maxW)); });
  }
  // 마지막 행 하변 = 행 전체 비례 스케일 (block-editor.html startRowScaleAll).
  function startRowScaleAll(rz, e) {
    const table = rz.closest(".content").querySelector(".lumir-tbl");
    const h0 = curRowHeights(table), H0 = h0.reduce((a, b) => a + b, 0) || 1, sy = e.clientY;
    document.body.style.cursor = "ns-resize";
    scaleDrag(table, (ev) => { const f = Math.max(0.1, (H0 + (ev.clientY - sy)) / H0); applyRowHeightsLive(table, h0.map((h) => Math.max(MIN_SCALE_ROW, Math.round(h * f)))); });
  }
  function startCornerScale(rz, e) {
    const table = rz.closest(".content").querySelector(".lumir-tbl");
    const c0 = curColWidths(table), h0 = curRowHeights(table);
    const W0 = c0.reduce((a, b) => a + b, 0) || 1, H0 = h0.reduce((a, b) => a + b, 0) || 1;
    const sx = e.clientX, sy = e.clientY;
    document.body.style.cursor = "nwse-resize";
    const maxW = maxTableWidth(table) || Infinity;
    scaleDrag(table, (ev) => {
      const fx = Math.max(0.1, (W0 + (ev.clientX - sx)) / W0);
      const fy = Math.max(0.1, (H0 + (ev.clientY - sy)) / H0);
      applyColWidthsLive(table, clampWidths(c0.map((w) => Math.max(MIN_SCALE_COL, Math.round(w * fx))), maxW));
      applyRowHeightsLive(table, h0.map((h) => Math.max(MIN_SCALE_ROW, Math.round(h * fy))));
    });
  }

  // ---------- 행/열 재정렬 (F3) — 그립 UI 는 제거됐으나 API(reorderRow/reorderCol)는 유지(툴바 이동 버튼 등에서 재사용 가능). ----------
  function reorderRow(table, from, to) {
    syncModelFromTableDom(table); // 재렌더 전 라이브 셀 편집을 모델로 회수(안 하면 미동기화 편집 유실)
    if (moveRow(table.__model, from, to)) { rerenderTable(table); addResizeHandles(table); onChange(); return true; }
    return false;
  }
  function reorderCol(table, from, to) {
    syncModelFromTableDom(table); // 재렌더 전 라이브 셀 편집을 모델로 회수(안 하면 미동기화 편집 유실)
    if (moveCol(table.__model, from, to)) { rerenderTable(table); addResizeHandles(table); onChange(); return true; }
    return false;
  }

  // ---------- cell navigation ----------
  function tableCells(table) { return [...table.querySelectorAll("td.lumir-cell")]; }
  function moveCell(table, cell, dir) {
    const cs = tableCells(table);
    const i = cs.indexOf(cell);
    const j = i + dir;
    if (j < 0) { focusEnd(cs[0]); return; }
    if (j >= cs.length) { runStructural(table, () => insertRow(table.__model, table.__model.G.length)); const c2 = tableCells(table); focusEnd(c2[i + 1] || c2[c2.length - 1]); return; }
    focusEnd(cs[j]);
  }
  function allMasters(table) {
    const m = table.__model, seen = new Set(), out = [];
    for (let r = 0; r < m.G.length; r++) for (let c = 0; c < m.G[r].length; c++) { const cell = m.G[r][c]; if (!seen.has(cell)) { seen.add(cell); out.push(cell); } }
    return out;
  }
  function onCellKeydown(e, td) {
    const table = td.closest(".lumir-tbl");
    // Ctrl/Cmd+A 단계 선택: 셀 전체 선택 → 이미 전체면 기본(문서) 위임 (F4)
    if ((e.metaKey || e.ctrlKey) && (e.key === "a" || e.key === "A")) {
      const m = table.__model;
      const masters = allMasters(table);
      if (new Set(selectedCells).size >= masters.length && masters.length > 0) return;
      e.preventDefault();
      const first = cellEl(table, m.G[0][0]);
      const lr = m.G[m.G.length - 1];
      const last = cellEl(table, lr[lr.length - 1]);
      if (first && last) selectRange(table, first, last);
      return;
    }
    // ESC: 셀 선택 해제(선택이 있을 때만 가로채고, 없으면 기본 동작 유지)
    if (e.key === "Escape" && selectedCells.length) {
      e.preventDefault();
      clearSel(table);
      return;
    }
    // 스페이스: 라인 시작 "[]"/"[ ]" → 체크박스 자동 변환(성공 시 스페이스 입력 취소)
    if (e.key === " " && (tryCellCheckboxAutoformat(td) || tryCellListAutoformat(td))) { e.preventDefault(); return; }
    // Backspace: 캐럿 바로 앞이 인라인 원자(체크박스/순서·불렛 마커)면 그 원자를 삭제.
    // 원자는 contenteditable=false + 뒤에 ZWSP 앵커라 브라우저 기본 Backspace로는 지워지지 않는다.
    if (e.key === "Backspace" && !isReadonly()) {
      const sel = window.getSelection && window.getSelection();
      if (sel && sel.rangeCount && sel.isCollapsed) {
        const r = sel.getRangeAt(0);
        if (td.contains(r.startContainer)) {
          let node;
          if (r.startContainer.nodeType === 1) node = r.startContainer.childNodes[r.startOffset - 1];
          else {
            const clean = (r.startContainer.nodeValue || "").replace(/​/g, "");
            if (r.startOffset > 0 && clean !== "") node = null; // 실제 문자 앞 → 기본 삭제
            else node = r.startContainer.previousSibling; // 줄 시작 또는 ZWSP-only 텍스트 → 그 앞을 검사
          }
          while (node && node.nodeType === 3 && (node.nodeValue || "").replace(/​/g, "") === "") node = node.previousSibling; // ZWSP/빈 텍스트 건너뜀
          if (isAtomEl(node)) {
            e.preventDefault();
            const parent = node.parentNode;
            let anchor = node.nextSibling;
            while (anchor && anchor.nodeType === 3 && (anchor.nodeValue || "").replace(/​/g, "") === "") { const t = anchor; anchor = anchor.nextSibling; t.remove(); } // 뒤따르는 ZWSP 앵커 제거
            node.remove();
            try {
              const nr = document.createRange();
              if (anchor) nr.setStartBefore(anchor); else nr.setStart(parent, parent.childNodes.length);
              nr.collapse(true);
              sel.removeAllRanges();
              sel.addRange(nr);
            } catch (_) { /* selection 미구현 환경 무시 */ }
            syncModelFromTableDom(table);
            onChange();
            refreshActiveTableOverlays();
            return;
          }
        }
      }
    }
    if (e.key === "Tab") { e.preventDefault(); moveCell(table, td, e.shiftKey ? -1 : 1); return; }
    if (e.key === "Enter") {
      // 셀 줄바꿈 — 셀은 white-space:normal이라 "\n" 텍스트는 공백으로 접힌다. <br>로 삽입해야 실제 개행.
      // (직렬화 htmlToInlineContent가 <br>→"\n" 변환, filler <br>는 무시 — regular 블록 handleHardBreak와 동일)
      e.preventDefault();
      // 리스트 연속: 현재 줄이 순서/불렛 마커로 시작하면 Enter가 다음 항목 자동 생성(빈 항목이면 리스트 종료).
      const li = currentLineInfo(td);
      if (li && li.markerEl) {
        const sel0 = window.getSelection();
        if (li.hasContent) {
          const r = sel0.getRangeAt(0);
          r.deleteContents();
          const br = document.createElement("br");
          r.insertNode(br);
          const marker = makeListEl(li.kind);
          br.after(marker);
          if (!marker.nextSibling) marker.after(document.createTextNode("​"));
          const nr = document.createRange();
          nr.setStartAfter(marker);
          nr.collapse(true);
          sel0.removeAllRanges();
          sel0.addRange(nr);
        } else {
          // 빈 리스트 항목에서 Enter → 마커 제거하고 리스트 종료(현재 줄을 일반 줄로).
          const anchorNext = li.markerEl.nextSibling;
          const parent = li.markerEl.parentNode;
          li.markerEl.remove();
          try {
            const nr = document.createRange();
            if (anchorNext) nr.setStartBefore(anchorNext);
            else nr.setStart(parent, parent.childNodes.length);
            nr.collapse(true);
            sel0.removeAllRanges();
            sel0.addRange(nr);
          } catch (_) { /* ignore */ }
        }
        onChange();
        refreshActiveTableOverlays();
        return;
      }
      const sel = window.getSelection();
      if (sel && sel.rangeCount) {
        const r = sel.getRangeAt(0);
        r.deleteContents();
        const br = document.createElement("br");
        r.insertNode(br);
        while (br.nextSibling && br.nextSibling.nodeType === 3 && br.nextSibling.nodeValue === "") br.nextSibling.remove();
        if (!br.nextSibling) {
          const filler = document.createElement("br");
          filler.setAttribute("data-lumir-br-filler", "");
          br.after(filler); // 줄 끝 caret이 새 줄에 놓이도록 보조 <br>(직렬화 무시)
        }
        r.setStartAfter(br);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        onChange();
        refreshActiveTableOverlays(); // 줄바꿈으로 셀 높이 증가 → 오버레이 재배치
      }
      return;
    }
    if (e.key === "ArrowDown") {
      const m = td.__master; const nr = m.r + m.rowspan;
      if (nr < table.__model.G.length) { e.preventDefault(); focusCellAt(table, nr, m.c); }
      else { const blk = table.closest(".block"); if (blk && focusAdjacentBlockEdge(blk, 1)) e.preventDefault(); } // 마지막 행 → 아래 블록
    }
    if (e.key === "ArrowUp") {
      const m = td.__master;
      if (m.r - 1 >= 0) { e.preventDefault(); focusCellAt(table, m.r - 1, m.c); }
      else { const blk = table.closest(".block"); if (blk && focusAdjacentBlockEdge(blk, -1)) e.preventDefault(); } // 첫 행 → 위 블록
    }
    // 좌우 셀 이동 — 셀 끝에서 →는 다음 셀, 셀 시작에서 ←는 이전 셀(읽기 순서, 표 끝/처음이면 그대로).
    // 셀 중간에서는 네이티브(셀 내부 텍스트 이동). 표 구조 변경 없음.
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount || !sel.isCollapsed) return;
      const range = sel.getRangeAt(0);
      const cs = tableCells(table);
      const i = cs.indexOf(td);
      if (i < 0) return;
      if (e.key === "ArrowRight" && caretAtCellEnd(td, range) && i + 1 < cs.length) { e.preventDefault(); focusStart(cs[i + 1]); }
      else if (e.key === "ArrowLeft" && caretAtCellStart(td, range) && i - 1 >= 0) { e.preventDefault(); focusEnd(cs[i - 1]); }
    }
  }

  // ---------- listeners ----------
  function onMousedown(e) {
    // 셀 안 체크박스 클릭 → checked 토글(직렬화 {type:"checkbox"} + 변경 알림). 캐럿 이동 방지 위해 preventDefault.
    const box = e.target.closest && e.target.closest(".lumir-cell-check");
    if (box) { toggleCellCheckbox(box, e); return; }
    const corner = e.target.closest && e.target.closest(".edge-corner");
    if (corner) { e.preventDefault(); startCornerScale(corner, e); return; }
    const eR = e.target.closest && e.target.closest(".edge-right");
    if (eR) { e.preventDefault(); startColScaleAll(eR, e); return; }
    const eB = e.target.closest && e.target.closest(".edge-bottom");
    if (eB) { e.preventDefault(); startRowScaleAll(eB, e); return; }
    const rz = e.target.closest && e.target.closest(".col-resizer");
    if (rz) { e.preventDefault(); startColResize(rz, e); return; }
    const rrz = e.target.closest && e.target.closest(".row-resizer");
    if (rrz) { e.preventDefault(); startRowResize(rrz, e); return; }
    const addRow = e.target.closest && e.target.closest(".tbl-addrow");
    if (addRow) { e.preventDefault(); const t = addRow.closest(".content").querySelector(".lumir-tbl"); runStructural(t, () => insertRow(t.__model, t.__model.G.length)); return; }
    const addCol = e.target.closest && e.target.closest(".tbl-addcol");
    if (addCol) { e.preventDefault(); const t = addCol.closest(".content").querySelector(".lumir-tbl"); if (canAddColumn(t)) runStructural(t, () => insertCol(t.__model, t.__model.G[0].length)); return; }
    const addBoth = e.target.closest && e.target.closest(".tbl-addboth");
    if (addBoth) { e.preventDefault(); const t = addBoth.closest(".content").querySelector(".lumir-tbl"); runStructural(t, () => { insertRow(t.__model, t.__model.G.length); if (canAddColumn(t)) insertCol(t.__model, t.__model.G[0].length); }); return; }
    const td = e.target.closest && e.target.closest("td.lumir-cell");
    if (td) { selAnchor = td; selTable = td.closest(".lumir-tbl"); selecting = true; }
  }
  function onMousemove(e) {
    if (!selecting) return;
    const td = e.target.closest && e.target.closest("td.lumir-cell");
    if (!td || td.closest(".lumir-tbl") !== selTable) return;
    if (td !== selAnchor) { window.getSelection().removeAllRanges(); selectRange(selTable, selAnchor, td); }
    else { clearSel(selTable); }
  }
  function onMouseup() { selecting = false; }
  function onFocusin(e) {
    const td = e.target.closest && e.target.closest("td.lumir-cell");
    if (td) {
      activeTable = td.closest(".lumir-tbl");
      activeCell = td;
      if (!selecting) clearSel(activeTable);
      addResizeHandles(activeTable);
      showToolbar(activeTable);
      updateToolbarStates();
    } else {
      const c = e.target.closest && e.target.closest(".content");
      if (!c || !c.querySelector || !c.querySelector(".lumir-tbl")) {
        // 표 밖으로 포커스 이동
        setTimeout(() => { const a = document.activeElement; if (!a || (!a.closest("td.lumir-cell") && !a.closest(".lumir-table-toolbar"))) hideToolbar(); }, 0);
      }
    }
  }
  function onKeydown(e) {
    // Ctrl/Cmd+C·X (셀 범위 선택) — 셀이 각각 독립 contenteditable이라 드래그 선택은 네이티브 선택을
    // 만들지 못하고 비운다. 그 결과 실제 Chrome은 Ctrl+C에서 copy 이벤트를 발화하지 않아 클립보드가
    // 갱신되지 않는다(직전 복사 데이터가 그대로 붙음). 포커스가 셀을 벗어난 경우까지 커버하도록 상위에서 처리.
    if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === "c" || e.key === "C" || e.key === "x" || e.key === "X")
        && selectedCells.length && activeTable && editorEl.contains(activeTable)) {
      e.preventDefault();
      copySelectedCells(activeTable, e.key === "x" || e.key === "X");
      return;
    }
    const td = e.target.closest && e.target.closest("td.lumir-cell");
    if (td) onCellKeydown(e, td);
  }
  // 표 툴바 표시 중, 표 바깥(다른 블록·빈 영역·에디터 밖) 클릭 → 툴바 닫기 + 선택 해제.
  // (contenteditable 셀은 비포커스 영역 클릭 시 포커스를 유지해 focusin 기반 숨김이 동작하지 않으므로 필요)
  function onDocMousedown(e) {
    if (!toolbarVisible()) return;
    const t = e.target;
    if (t && t.closest && t.closest(".lumir-table-toolbar")) return; // 툴바 조작은 유지
    const host = activeTable && activeTable.closest ? activeTable.closest(".content") : null;
    if (host && t && host.contains(t)) return; // 활성 표(셀·리사이즈 핸들 포함) 내부 클릭은 유지
    const prev = activeTable;
    hideToolbar();
    if (prev) clearSel(prev);
    activeTable = null;
    activeCell = null;
  }

  // 창 크기 변경 시 표가 리플로우되면 body-absolute 툴바와 픽셀 좌표 핸들이 stale해지므로 재배치.
  /** 활성 표의 오버레이(행/열 추가 바·리사이즈 핸들·툴바)를 현재 표 크기에 맞춰 재배치. */
  function refreshActiveTableOverlays() {
    if (!toolbarVisible() || !activeTable || !editorEl.contains(activeTable)) return;
    addResizeHandles(activeTable);
    showToolbar(activeTable);
  }
  function onWinResize() { refreshActiveTableOverlays(); }

  // 셀 내용 편집(타이핑 등)으로 표 높이/너비가 바뀌면 오버레이가 어긋난다(재포커스 전까지 stale).
  // input을 rAF로 디바운스해 활성 표의 오버레이를 재배치한다. 프로그램적 편집(체크박스 삽입·Enter)은
  // input을 발화하지 않으므로 각 지점에서 refreshActiveTableOverlays()를 직접 호출한다.
  let overlayRaf = 0;
  function onCellInput(e) {
    const t = e.target;
    const td = t && t.closest && t.closest("td.lumir-cell");
    if (!td || !activeTable || td.closest(".lumir-tbl") !== activeTable) return;
    if (overlayRaf) return;
    const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (cb) => setTimeout(cb, 0);
    overlayRaf = raf(() => { overlayRaf = 0; refreshActiveTableOverlays(); });
  }

  // ---------- 클립보드: 셀 범위 복사/잘라내기 + 셀 안 그리드 붙여넣기 (스프레드시트식) ----------
  // 클립 3종 기록(무손실 lumir-JSON + html <table> + TSV). selBBox는 병합 인지(getTargets→expandRect).
  function writeCellClip(e, table) {
    syncModelFromTableDom(table);
    const rect = expandRect(table.__model, selBBox(table));
    const clip = extractGridClip(table.__model, rect);
    if (e.clipboardData && e.clipboardData.setData) {
      try {
        e.clipboardData.setData(LUMIR_TABLE_MIME, JSON.stringify(clip));
        e.clipboardData.setData("text/html", gridClipToHtml(clip));
        e.clipboardData.setData("text/plain", gridClipToTSV(clip));
      } catch (_) {}
    }
    e.preventDefault();
    return clip;
  }
  function onCopy(e) {
    // 셀 '범위' 선택일 때만 가로챔 — 셀 안 텍스트 복사는 네이티브 유지.
    if (!activeTable || !editorEl.contains(activeTable) || !selectedCells.length) return;
    writeCellClip(e, activeTable);
  }
  function clearSelectedCellContent(table) {
    [...new Set(selectedCells)].forEach((m) => { m.content = []; }); // 구조 유지, 내용만 비움
    rerenderTable(table);
    clearSel(table);
    addResizeHandles(table);
    onChange();
  }
  function onCut(e) {
    if (!activeTable || !editorEl.contains(activeTable) || !selectedCells.length) return;
    const table = activeTable;
    writeCellClip(e, table);
    clearSelectedCellContent(table);
  }
  // 키보드(Ctrl/Cmd+C·X) 진입점 — 셀이 각각 독립 contenteditable이라 드래그 선택은 네이티브 선택을 만들지
  // 못하고, 실제 Chrome은 그 상태에서 copy 이벤트를 발화하지 않는다(클립보드 미갱신). 그래서 copy 이벤트에
  // 의존하지 않는 비동기 Clipboard API로 직접 기록한다. 무손실 클립은 gridClipToHtml이 html에 임베드하므로
  // text/html + text/plain 만으로도 완전 복원된다(커스텀 MIME 불요). 구형/차단 시 execCommand 폴백.
  function copySelectedCells(table, cut) {
    if (!selectedCells.length) return;
    syncModelFromTableDom(table);
    const clip = extractGridClip(table.__model, expandRect(table.__model, selBBox(table)));
    const html = gridClipToHtml(clip);
    const tsv = gridClipToTSV(clip);
    let wrote = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.write === "function" && typeof window.ClipboardItem === "function") {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([tsv], { type: "text/plain" }),
        });
        navigator.clipboard.write([item]).catch(() => {});
        wrote = true;
      }
    } catch (_) {}
    if (!wrote) {
      // 폴백: 앵커 셀을 네이티브 선택 후 execCommand('copy') → onCopy가 3종 MIME 기록.
      try {
        const anchorEl = cellEl(table, [...new Set(selectedCells)][0]);
        if (anchorEl && typeof document.execCommand === "function") {
          const sel = window.getSelection();
          const range = document.createRange();
          range.selectNode(anchorEl);
          sel.removeAllRanges();
          sel.addRange(range);
          document.execCommand("copy");
          sel.removeAllRanges();
        }
      } catch (_) {}
    }
    if (cut) clearSelectedCellContent(table);
  }
  function onPaste(e) {
    const td = e.target && e.target.closest && e.target.closest("td.lumir-cell");
    if (!td) return; // 셀 밖 → editor.js 붙여넣기(새 표 블록 등)가 담당
    const table = td.closest(".lumir-tbl");
    if (!table || !table.__model || !editorEl.contains(table)) return;
    const cd = e.clipboardData;
    if (!cd || !cd.getData) return;
    const clip = parseClipToGrid({ lumir: cd.getData(LUMIR_TABLE_MIME), html: cd.getData("text/html"), text: cd.getData("text/plain") });
    if (!clip) return; // 그리드가 아니면 네이티브 통과(셀 안 일반 텍스트 붙여넣기, 현행 유지)
    e.preventDefault();
    syncModelFromTableDom(table);
    const anchor = { r: td.__master.r, c: td.__master.c };
    // 열 자동확장을 에디터 가용폭 예산으로 제한 — 초과 열은 잘려 표가 편집영역을 벗어나지 않는다(canAddColumn과 동일 기준).
    const maxW = maxTableWidth(table);
    const maxCols = maxW ? Math.max(1, Math.floor(maxW / HARD_MIN_COL)) : 0;
    pasteGridIntoModel(table.__model, anchor, clip, { maxCols });
    rerenderTable(table);
    if (clampTableWidth(table)) rerenderTable(table); // 자동확장분을 폭예산으로 재조정(no-scroll 유지)
    clearSel(table);
    const at = table.__model.G[anchor.r] && table.__model.G[anchor.r][anchor.c];
    if (at) focusCell(table, at);
    addResizeHandles(table);
    onChange();
    if (toolbarVisible() && activeTable === table) { showToolbar(table); updateToolbarStates(); }
  }

  editorEl.addEventListener("mousedown", onMousedown);
  document.addEventListener("mousemove", onMousemove);
  document.addEventListener("mouseup", onMouseup);
  document.addEventListener("mousedown", onDocMousedown);
  editorEl.addEventListener("focusin", onFocusin);
  editorEl.addEventListener("keydown", onKeydown);
  editorEl.addEventListener("copy", onCopy);
  editorEl.addEventListener("cut", onCut);
  editorEl.addEventListener("paste", onPaste);
  window.addEventListener("resize", onWinResize);
  editorEl.addEventListener("input", onCellInput);

  return {
    toolbarEl: bar,
    addResizeHandles,
    reorderRow,
    reorderCol,
    destroy() {
      editorEl.removeEventListener("mousedown", onMousedown);
      document.removeEventListener("mousemove", onMousemove);
      document.removeEventListener("mouseup", onMouseup);
      document.removeEventListener("mousedown", onDocMousedown);
      editorEl.removeEventListener("focusin", onFocusin);
      editorEl.removeEventListener("keydown", onKeydown);
      editorEl.removeEventListener("copy", onCopy);
      editorEl.removeEventListener("cut", onCut);
      editorEl.removeEventListener("paste", onPaste);
      window.removeEventListener("resize", onWinResize);
      editorEl.removeEventListener("input", onCellInput);
      if (bar.parentNode) bar.parentNode.removeChild(bar);
    },
  };
}
